import {
  Injectable,
  signal,
  computed,
  inject,
  PLATFORM_ID,
  DestroyRef,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Router } from '@angular/router';
import { environment } from '../../../environments/environment';
import { AppStorageService } from './app-storage.service';
import { MenuService, MenuItem, MenuChild } from './menu.service';
import { TenantStore } from '../../stores/tenant.store';
import { SharedAuthStateService } from '../../stores/shared-auth-state.service';

/**
 * A single command-palette result.
 *   - `kind` colours the icon and groups results.
 *   - `keywords` augments the searchable surface (use for synonyms /
 *     legacy names; the label itself is always searched).
 *   - `run` either navigates (`{ route: 'symbols' }`) or calls a function.
 */
export type PaletteItemKind = 'route' | 'symbol' | 'action' | 'recent';

export interface PaletteItem {
  readonly id: string;
  readonly label: string;
  readonly kind: PaletteItemKind;
  readonly description?: string;
  readonly keywords?: readonly string[];
  readonly hotkey?: string;
  readonly run: () => void | Promise<void>;
}

interface RecentEntry {
  readonly id: string;
  readonly label: string;
  readonly route?: readonly string[];
  readonly timestamp: number;
}

const RECENT_KEY = 'razor:cmdk:recent';
const RECENT_CAP = 10;

/**
 * Centralised state for the ⌘K command palette (GAP-005).
 *
 * Exposes:
 *   - `isOpen` / `open` / `close` / `toggle` — modal lifecycle.
 *   - `query` — current search string (consumers update via `setQuery`).
 *   - `results` — items filtered by the query.
 *   - `recent` — last 10 successful visits / actions, persisted to
 *     localStorage so power users see them on first ⌘K of a session.
 *   - `registerItems` — features can contribute items at bootstrap.
 *
 * SSR-safe (no DOM access during construction). Defers any localStorage
 * reads to the first browser-side `recent()` invocation.
 */
@Injectable({ providedIn: 'root' })
export class CommandPaletteService {
  private readonly router = inject(Router);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly destroyRef = inject(DestroyRef);
  private readonly appStorage = inject(AppStorageService);
  private readonly menu = inject(MenuService);
  private readonly tenants = inject(TenantStore);
  private readonly auth = inject(SharedAuthStateService);

  private readonly _isOpen = signal(false);
  readonly isOpen = this._isOpen.asReadonly();

  private readonly _query = signal('');
  readonly query = this._query.asReadonly();

  private readonly registeredItems = signal<readonly PaletteItem[]>([]);
  private readonly _recent = signal<readonly RecentEntry[]>([]);
  readonly recent = this._recent.asReadonly();

  /**
   * The route-derived items. Seeded from the router config on first use
   * so feature modules don't need to opt-in individually.
   */
  private readonly routeItems = computed<readonly PaletteItem[]>(() => this.seedRouteItems());

  /**
   * Recent entries resolved against the live item pool (routes + registered
   * actions), newest first, re-kinded as 'recent'. Entries whose id no longer
   * resolves (renamed route, feature gated off, action not registered for this
   * user) drop out — so consumers (the palette AND the sidebar Recent rail)
   * only ever surface rows that can actually be invoked.
   */
  readonly resolvedRecent = computed<readonly PaletteItem[]>(() => {
    const lookup = new Map<string, PaletteItem>();
    for (const item of [...this.routeItems(), ...this.registeredItems()]) {
      lookup.set(item.id, item);
    }
    return this._recent()
      .map(e => lookup.get(e.id))
      .filter((v): v is PaletteItem => v != null)
      .map(item => ({ ...item, kind: 'recent' as const }));
  });

  /**
   * All items in scope, recents first. Items already surfaced under "recent"
   * are excluded from the route/action sections so each id appears exactly
   * once (duplicate ids also break `track item.id` in the palette's @for).
   */
  readonly results = computed<readonly PaletteItem[]>(() => {
    const q = this._query().trim().toLowerCase();
    const recents = this.resolvedRecent();
    const recentIds = new Set(recents.map(r => r.id));
    const base = [
      ...recents,
      ...this.routeItems().filter(i => !recentIds.has(i.id)),
      ...this.registeredItems().filter(i => !recentIds.has(i.id)),
    ];
    if (!q) return base.slice(0, 50);
    return base.filter(item => this.matches(item, q)).slice(0, 50);
  });

  constructor() {
    this.loadRecent();

  }

  open(): void {
    this._isOpen.set(true);
  }

  close(): void {
    this._isOpen.set(false);
    this._query.set('');
  }

  toggle(): void {
    if (this._isOpen()) this.close();
    else this.open();
  }

  setQuery(value: string): void {
    this._query.set(value);
  }

  /** Append items to the pool searchable by ⌘K (e.g. dynamic actions). */
  registerItems(items: readonly PaletteItem[]): void {
    this.registeredItems.update(existing => {
      const byId = new Map(existing.map(i => [i.id, i]));
      for (const item of items) byId.set(item.id, item);
      return [...byId.values()];
    });
  }

  /**
   * GAP-CP-021-A · Withdraw previously registered items by id. Features whose
   * availability is REACTIVE (e.g. the copilot entry, gated on a runtime
   * status flag + permission) call this when their gate closes so the palette
   * never surfaces an action that would dead-click. `resolvedRecent` already
   * drops recents whose id no longer resolves, so the sidebar Recent rail
   * self-heals on the same call.
   */
  unregisterItems(ids: readonly string[]): void {
    if (ids.length === 0) return;
    const drop = new Set(ids);
    this.registeredItems.update(existing => existing.filter(i => !drop.has(i.id)));
  }

  /**
   * Execute a palette item: run its `run()` callback, record it as recent,
   * and close the palette.
   */
  invoke(item: PaletteItem): void {
    // Re-resolve on activation: a role/tenant change can retire a visible result.
    const current = [...this.routeItems(), ...this.registeredItems()].find(candidate => candidate.id === item.id);
    if (!current) return;
    this.trackRecent(current);
    void current.run();
    this.close();
  }

  /**
   * Record a route visit (called from app-component on navigation). The
   * palette uses this to surface jump-back options under "Recent".
   */
  trackRouteVisit(commands: readonly string[], label: string): void {
    this.trackRecent({
      id: `route:${commands.join('/')}`,
      label,
      kind: 'recent',
      run: () => { void this.router.navigate([...commands]); },
    });
  }

  /**
   * Record an item as "recent" WITHOUT executing it — for features that open
   * via their own affordance (e.g. the copilot's floating launcher) yet should
   * still surface in the sidebar Recent rail. `item.id` should match a
   * registered palette item so the rail stays consistent; `run` is never
   * invoked here — recording captures only id + label + timestamp.
   */
  recordRecent(item: PaletteItem): void {
    this.trackRecent(item);
  }

  private trackRecent(item: PaletteItem): void {
    if (!isPlatformBrowser(this.platformId)) return;
    const entry: RecentEntry = {
      id: item.id,
      label: item.label,
      timestamp: Date.now(),
    };
    this._recent.update(current => {
      const filtered = current.filter(e => e.id !== entry.id);
      const next = [entry, ...filtered].slice(0, RECENT_CAP);
      this.persistRecent(next);
      return next;
    });
  }

  private matches(item: PaletteItem, q: string): boolean {
    if (item.label.toLowerCase().includes(q)) return true;
    if (item.description?.toLowerCase().includes(q)) return true;
    if (item.keywords?.some(k => k.toLowerCase().includes(q))) return true;
    return false;
  }

  private loadRecent(): void {
    // GAP-FE-C013: SSR-safe, quota-resilient wrapper; preserves RECENT_KEY and the
    // RECENT_CAP slice (getJSON returns null on server / miss / corrupt).
    const parsed = this.appStorage.getJSON<RecentEntry[]>(RECENT_KEY);
    if (Array.isArray(parsed)) {
      this._recent.set(parsed.slice(0, RECENT_CAP));
    }
  }

  private persistRecent(entries: readonly RecentEntry[]): void {
    // GAP-FE-C013: SSR-safe, quota-resilient wrapper; preserves the RECENT_KEY key.
    this.appStorage.setJSON(RECENT_KEY, entries);
  }

  /** Use the same caller-shaped, entitlement-filtered navigation and labels as the sidebar. */
  private seedRouteItems(): readonly PaletteItem[] {
    const items = new Map<string, PaletteItem>();
    const visit = (entries: readonly (MenuItem | MenuChild)[]): void => {
      for (const item of entries) {
        if (item.route) {
          const path = item.route.replace(/^\//, '');
          const flag = COMMAND_PALETTE_GATED_PATHS[path.split('/')[0]];
          if (flag && environment.features[flag] !== true) continue;
          if (path === 'auth' || path.startsWith('auth/') || path.includes(':')) continue;
          const id = `route:${path}`;
          items.set(id, {
            id, label: item.label, kind: 'route', keywords: [path],
            run: () => { void this.router.navigate(['/' + path]); },
          });
        }
        if (item.children?.length) visit(item.children);
      }
    };
    for (const group of this.menu.visibleMenu()) {
      if (!group.feature || this.tenants.hasAccessToFeature(group.feature)) visit(group.items);
    }
    // Core search is tenant-scoped but has no role gate and no sidebar row.
    if (this.auth.isAuthenticated() && (this.tenants.currentTenant()?.id ?? this.auth.currentTenantId())) {
      items.set('route:search', { id: 'route:search', label: 'Global Search', kind: 'route', keywords: ['search', 'find'], run: () => { void this.router.navigate(['/search']); } });
    }
    return [...items.values()];
  }
}

/**
 * GAP-FE-H003 · Mirror of `FLAG_GATED_ITEM_IDS` in
 * `core/api/services/menu-feature-api.service.ts`. Keys are the top-level
 * route segment; values are the `environment.features` flag that
 * must be `true` for the route to appear in the command palette.
 */
const COMMAND_PALETTE_GATED_PATHS: Record<string, keyof typeof environment.features> = {
  'copilot-summaries': 'enableCopilotSummaries',
  'agent-proposals': 'enableAgentProposals',
  'agent-controls': 'enableAgentControls',
  'agent-invariants': 'enableAgentInvariants',
};
